import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios'
import { toast } from 'sonner'
import { useAuthStore } from '@/stores/useAuthStore'
import { AUTH } from '@/api/endpoints'
import { getErrorMessage } from '@/lib/getErrorMessage'
import { useMaintenanceStore } from '@/stores/useMaintenanceStore'
import { useBillingLockStore } from '@/stores/useBillingLockStore'
import { apiRoot } from '@/lib/tenantHost'

/** The versioned API root, e.g. https://api.kaamco.com/api/v1 (or same-origin on a custom domain). */
export const API_BASE_URL = `${apiRoot()}/v1`
const baseURL = API_BASE_URL

export const apiClient = axios.create({
  baseURL,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,
})

export interface ExtendedRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean
  _isRefreshRequest?: boolean
  _isSessionCheck?: boolean
}

apiClient.interceptors.request.use((config) => {
  if (config.data instanceof FormData) {
    // Let the browser set the multipart boundary. Axios 1.x normally exposes
    // headers as AxiosHeaders, so deleting the property directly is not
    // reliable in every browser/build combination.
    if (typeof config.headers?.delete === 'function') {
      config.headers.delete('Content-Type')
    } else if (config.headers) {
      delete config.headers['Content-Type']
    }
  }
  if (typeof config.url === 'string' && config.url.length > 1 && config.url.endsWith('/')) {
    config.url = config.url.slice(0, -1)
  }
  return config
})

/**
 * A single in-flight refresh shared by every request that 401s at the same
 * time. Without this, N concurrent failures each fire their own refresh and
 * each retry, which multiplies one expired session into a request storm.
 */
let refreshPromise: Promise<void> | null = null

function runRefresh(): Promise<void> {
  refreshPromise ??= (async () => {
    const refreshResponse = await axios.post(
      `${baseURL}${AUTH.REFRESH}`,
      {},
      {
        withCredentials: true,
        headers: {
          Accept: 'application/json',
        },
      }
    )

    // If the backend redirects unauthorized requests to an HTML login page,
    // treat that as a failed refresh to avoid repeated retries.
    const contentType = refreshResponse.headers?.['content-type']
    if (typeof contentType === 'string' && contentType.toLowerCase().includes('text/html')) {
      throw new Error('Refresh returned HTML (login redirect)')
    }
    // The refresh rotates the server-side sessionId, which strands the live
    // socket on its now-defunct session. Tell the socket layer to reconnect
    // so it re-handshakes (and re-binds presence) under the new session.
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new Event('auth:token-refreshed'))
    }
  })().finally(() => {
    refreshPromise = null
  })
  return refreshPromise
}

/**
 * The refresh was answered and the session is gone: a 401, or a redirect to an
 * HTML login page. Anything else may succeed on the next try.
 */
function isSessionRefused(refreshError: unknown): boolean {
  if (axios.isAxiosError(refreshError)) {
    return refreshError.response?.status === 401
  }
  return refreshError instanceof Error && refreshError.message.startsWith('Refresh returned HTML')
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as ExtendedRequestConfig | undefined
    // No response at all (server down, DNS failure, CORS, aborted): there is no
    // session to refresh, so fail fast. Retrying here is what let a stopped
    // backend produce an unbounded stream of identical requests.
    if (!error.response) {
      return Promise.reject(error)
    }
    // The platform is down for maintenance. This is not a failure the user can
    // act on, so it is routed to the maintenance screen rather than surfaced as
    // an error toast. MaintenanceScreen polls the public endpoint and clears
    // itself once the window closes.
    if (error.response.status === 503) {
      const body = error.response.data as
        | { error?: string; maintenance?: { message?: string; startsAt?: string; endsAt?: string } }
        | undefined
      if (body?.error === 'MaintenanceMode') {
        useMaintenanceStore.getState().lockOut({
          state: 'active',
          message: body.maintenance?.message,
          startsAt: body.maintenance?.startsAt,
          endsAt: body.maintenance?.endsAt,
        })
        return Promise.reject(error)
      }
    }

    // The workspace is read-only for billing: the trial ended without a plan,
    // or the subscription ended or went unpaid. Keyed on the status alone,
    // because the error filter does not pass the error code through. One toast
    // however many writes fail at once; the banner carries the next step.
    if (error.response.status === 402) {
      useBillingLockStore.getState().markRefused()
      toast.error('This workspace is read-only until billing is sorted out. Nothing was saved.', {
        id: 'billing-read-only',
      })
      return Promise.reject(error)
    }

    // Inside a KAAMCO support session every write is refused. Said once per
    // attempt in plain words, rather than as a generic permission error.
    if (error.response.status === 403) {
      const body = error.response.data as { code?: string; message?: string } | undefined
      if (body?.code === 'SUPPORT_SESSION_READ_ONLY') {
        toast.info('Read-only support session: changes are switched off.', {
          id: 'support-session-read-only',
        })
        return Promise.reject(error)
      }
    }

    if (error.response.status !== 401 || !originalRequest) {
      return Promise.reject(error)
    }
    if (originalRequest._isSessionCheck === true) {
      return Promise.reject(error)
    }
    const isLoginRequest =
      typeof originalRequest.url === 'string' && originalRequest.url.includes('/auth/login')
    if (isLoginRequest) {
      return Promise.reject(error)
    }
    if (originalRequest._retry === true || originalRequest._isRefreshRequest === true) {
      useAuthStore.getState().logout()
      const message =
        (error.response?.data as { message?: string })?.message ||
        'Your session has expired or is invalid. Please sign in again.'
      toast.error(message)
      return Promise.reject(error)
    }
    originalRequest._retry = true
    try {
      await runRefresh()
    } catch (refreshError) {
      // Only the server refusing the session signs the person out. Offline, a
      // deploy restarting the API or a server error says nothing about the
      // session, so they stay signed in and the next request tries again.
      if (!isSessionRefused(refreshError)) {
        return Promise.reject(error)
      }
      useAuthStore.getState().logout()
      const message: string =
        (error.response?.data as { message?: string })?.message ||
        (refreshError != null ? getErrorMessage(refreshError) : '') ||
        'Your session has expired or is invalid. Please sign in again.'
      toast.error(message)
      return Promise.reject(error)
    }
    return apiClient.request(originalRequest)
  }
)
